import { useEffect, useState } from 'react';
import { adminAPI } from '@/lib/adminApi';
import { Users, UserCheck, Clock, FileText, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.overview().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-purple-200/50">Loading…</div>;
  if (!data) return <div className="p-10 text-red-300">Failed to load.</div>;

  const stats = [
    { label: 'Total Users', value: data.total_users, icon: Users, color: '#a855f7' },
    { label: 'Active Users', value: data.active_users, icon: UserCheck, color: '#22c55e' },
    { label: 'New (7 days)', value: data.recent_signups_7d, icon: TrendingUp, color: '#3b82f6' },
    { label: 'Onboarding Done', value: data.completed_onboarding, icon: UserCheck, color: '#06b6d4' },
    { label: 'Pending Onboarding', value: data.pending_onboarding, icon: Clock, color: '#f59e0b' },
    { label: 'Awaiting Reports', value: data.awaiting_reports, icon: FileText, color: '#ec4899' },
  ];

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>Dashboard</h1>
        <p className="text-purple-200/50 text-sm mt-1">At-a-glance metrics for Brand OS.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/[0.03] border border-white/5 rounded-2xl p-5"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <Icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <p className="text-3xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-purple-200/50 mt-1">{s.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Popularity */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4">Category Popularity</h3>
          <div className="space-y-3">
            {data.category_popularity.map(cat => {
              const max = Math.max(...data.category_popularity.map(c => c.count), 1);
              const pct = (cat.count / max) * 100;
              return (
                <div key={cat.slug}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white/80">{cat.name}</span>
                    <span className="text-purple-200/50">{cat.count} users</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: cat.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Signups */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-semibold">Recent Signups</h3>
            <button onClick={() => navigate('/skyportal/users')} className="text-xs text-purple-300 hover:text-purple-200">View all →</button>
          </div>
          <div className="space-y-2">
            {data.recent_users.length === 0 && <p className="text-purple-200/40 text-sm">No users yet.</p>}
            {data.recent_users.map(u => (
              <button
                key={u.id}
                onClick={() => navigate(`/skyportal/users/${u.id}`)}
                className="w-full flex items-center justify-between bg-white/[0.02] border border-white/5 rounded-lg p-3 hover:bg-white/[0.05] transition text-left"
              >
                <div>
                  <p className="text-white text-sm font-medium">{u.name || 'Unknown'}</p>
                  <p className="text-purple-200/50 text-xs">{u.email}</p>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] uppercase tracking-wider ${u.onboarding_completed ? 'text-green-400' : 'text-yellow-400'}`}>
                    {u.onboarding_completed ? 'Onboarded' : 'Pending'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
